import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { AxiosError } from 'axios'
import type { ChatwootUser } from '@/types/chatwoot'
import { signIn as apiSignIn, fetchProfile } from '@/api/auth'
import { setToken } from '@/api/chatwoot'
import { useKanbanConfigStore } from './kanban-config'

const STORAGE_KEY = 'kanban.auth.v1'
const SSO_COOKIE = 'kanban_atom_token'

function readCookie(name: string): string | null {
  const match = document.cookie.match(
    new RegExp('(?:^|;\\s*)' + name + '=([^;]+)'),
  )
  return match ? decodeURIComponent(match[1]) : null
}

interface PersistedAuth {
  user: ChatwootUser
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<ChatwootUser | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  const isAuthenticated = computed(() => user.value !== null)
  const token = computed(() => user.value?.access_token ?? null)

  // Admin = role 'administrator' no Chatwoot OU user.id está em
  // supervisorUserIds da config remota (supervisores extras). Reativo a
  // mudanças no config.
  const isAdmin = computed(() => {
    if (!user.value) return false
    if (user.value.role === 'administrator') return true
    const config = useKanbanConfigStore()
    return config.supervisorUserIds.includes(user.value.id)
  })

  function persist(value: ChatwootUser | null) {
    if (value) {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ user: value } satisfies PersistedAuth),
      )
    } else {
      sessionStorage.removeItem(STORAGE_KEY)
    }
  }

  function hydrate(): void {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return
    try {
      const parsed = JSON.parse(raw) as PersistedAuth
      if (parsed?.user?.access_token) {
        user.value = parsed.user
        setToken(parsed.user.access_token)
      }
    } catch {
      sessionStorage.removeItem(STORAGE_KEY)
    }
  }

  async function login(email: string, password: string): Promise<boolean> {
    loading.value = true
    error.value = null
    try {
      const result = await apiSignIn({ email, password })
      user.value = result
      setToken(result.access_token)
      persist(result)
      return true
    } catch (e) {
      const axiosError = e as AxiosError<{ errors?: string[]; message?: string }>
      const data = axiosError.response?.data
      const msg =
        data?.errors?.[0] ??
        data?.message ??
        axiosError.message ??
        'Falha no login'
      error.value = msg
      return false
    } finally {
      loading.value = false
    }
  }

  function logout(): void {
    user.value = null
    setToken(null)
    persist(null)
  }

  // SSO: reaproveita a sessão do Chatwoot (chat.pablomendes...) via cookie
  // cross-subdomain `kanban_atom_token` emitido pelo chatwoot-injector.
  // Cookie contém apenas o access_token permanente do usuário (não devise
  // creds), validado via GET /api/v1/profile.
  async function trySSO(): Promise<boolean> {
    if (user.value !== null) return true
    const token = readCookie(SSO_COOKIE)
    if (!token) return false
    try {
      const profile = await fetchProfile(token)
      user.value = profile
      setToken(token)
      persist(profile)
      return true
    } catch {
      return false
    }
  }

  return {
    user,
    loading,
    error,
    isAuthenticated,
    isAdmin,
    token,
    hydrate,
    login,
    logout,
    trySSO,
  }
})
